import {createRouter, createWebHistory} from 'vue-router';
// import { AuthService } from './services/auth.service';
import { store, higherManagementUserIds, canUseLeave } from './stores/store';
import { swell } from './views/swell/_store';
import { pulse } from './views/pulse/_store';
import { smAccess } from './views/management/stars-magnet/_store';

const routes = [
    {
      path: "/",
      name: "login",
      exact: true,
      component:  () => import('./views/auth/LoginView.vue'),
    },
    {
      path: "/login-check",
      name: "login-check",
      component: () => import("./views/auth/LoginCheckView.vue"),
    },
    {
      path: "/logout",
      name: "logout",
      component: () => import("./views/auth/LogoutView.vue"),
    },
    {
      path: "/campaign",
      name: "campaign",
      children: [
        {
          path: "",
          name: "index",
          component: () => import("./views/campaign/IndexView.vue"),
        },
        {
          path: ":id",
          name: "detail",
          component: () => import("./views/campaign/DetailView.vue"),
        },
      ],
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: "/campaign/post/:id",
      name: "post",
      component: () => import("./views/campaign/PreviewPostView.vue"),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: "/discovery",
      name: "discovery",
      children: [
        {
          path: "",
          name: "discovery-influencer",
          component: () => import("./views/discovery/IndexView.vue"),
        },
      ],
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: "/management",
      name: "management",
      children: [
        {
          path: "staff",
          name: "staff",
          component: () => import("./views/management/staff/IndexView.vue"),
        },
        {
          path: "influencer",
          name: "influencer",
          component: () =>
            import("./views/management/influencer/IndexView.vue"),
        },
        {
          path: "influencer-discovery",
          name: "influencer-discovery",
          component: () =>
            import("./views/management/influencer/DiscoveryView.vue"),
        },
        {
          path: "influencer-duplicate",
          name: "influencer-duplicate",
          component: () =>
            import("./views/management/influencer/DuplicateView.vue"),
        },
        {
          path: "management-campaign",
          name: "management-campaign",
          component: () => import("./views/management/campaign/IndexView.vue"),
        },
        {
          path: "finance",
          name: "finance",
          component: () => import("./views/management/finance/IndexView.vue"),
        },
        {
          path: "client",
          name: "client",
          component: () => import("./views/management/client/IndexView.vue"),
        },
        {
          path: "brand",
          name: "brand",
          component: () => import("./views/management/brand/IndexView.vue"),
        },
        {
          path: "blog",
          name: "blog",
          component: () => import("./views/management/blog/IndexView.vue"),
        },
        {
          path: "influencer-agency",
          name: "influencer-agency",
          component: () => import("./views/management/influencer-agency/IndexView.vue"),
        },
        {
          path: "impact-dashboard",
          name: "management-impact-dashboard",
          component: () => import("./views/management/campaign/ImpactDashboardView.vue"),
        },
        {
          path: "tracker",
          name: "management-tracker",
          component: () => import("./views/management/campaign/TrackerDashboard.vue"),
        },
        {
          // Performance dashboards. The team list is the entry point; the
          // individual board is addressed by the staff member's user id so it
          // can be linked to from anywhere (tracker, Pulse, a notification).
          path: "performance",
          name: "management-performance",
          component: () =>
            import("./views/management/performance/TeamPerformanceView.vue"),
        },
        {
          path: "performance/:userId",
          name: "management-performance-staff",
          component: () =>
            import("./views/management/performance/StaffPerformanceView.vue"),
        },
        {
          // Leave lives in Management › Staff › Leave. This path is kept for
          // notification links (/management/leave?tab=requests&review=<id>).
          path: "leave",
          name: "management-leave",
          redirect: (to) => ({
            name: "staff",
            query: { tab: "leave", view: to.query.view || to.query.tab || undefined, review: to.query.review || undefined },
          }),
        },
        {
          path: "leave/staff/:userId",
          name: "management-leave-staff",
          component: () => import("./views/management/leave/EmployeeLeaveView.vue"),
          meta: { requiresLeave: true },
        },
        {
          // My profile: my responsibilities and my leave. In testing with the leave module,
          // so it shares its switch (canUseLeave / requiresLeave).
          path: "my-profile",
          name: "management-my-profile",
          component: () => import("./views/management/profile/MyProfileView.vue"),
          meta: { requiresLeave: true },
        },
        {
          // Old My leave page, kept for notification links (/management/my-leave?review=<id>)
          path: "my-leave",
          name: "management-my-leave",
          redirect: (to) => ({ name: "management-my-profile", query: { tab: "leave", review: to.query.review || undefined } }),
        },
        {
          // Stars Magnet talent recruitment. Members only (sm_member); the
          // database enforces the same through sm_is_member().
          path: "stars-magnet",
          name: "management-stars-magnet",
          component: () => import("./views/management/stars-magnet/RecruitmentView.vue"),
          meta: { requiresStarsMagnet: true },
        },
        {
          path: "insights",
          name: "management-insights",
          component: () => import("./views/management/campaign/InsightsChatView.vue"),
          meta: {
            requiresHigherManagement: true,
          },
        },

      ],
      meta: {
        requiresAuth: true,
        isStaff: true,
      },
    },
    {
      path: "/swell",
      component: () => import("./views/swell/SwellLayout.vue"),
      children: [
        {
          path: "",
          name: "swell",
          redirect: { name: "swell-leads" },
        },
        {
          path: "leads",
          name: "swell-leads",
          component: () => import("./views/swell/LeadsView.vue"),
        },
        {
          path: "signals",
          name: "swell-signals",
          component: () => import("./views/swell/SignalsView.vue"),
        },
        {
          path: "discovery",
          name: "swell-discovery",
          component: () => import("./views/swell/DiscoveryView.vue"),
        },
        {
          path: "pipeline",
          name: "swell-pipeline",
          component: () => import("./views/swell/PipelineView.vue"),
        },
        {
          path: "insights",
          name: "swell-insights",
          component: () => import("./views/swell/InsightsView.vue"),
        },
        {
          path: "roster",
          name: "swell-roster",
          component: () => import("./views/swell/RosterView.vue"),
        },
        {
          path: "contacts",
          name: "swell-contacts",
          component: () => import("./views/swell/ContactsView.vue"),
        },
        {
          path: "brands",
          name: "swell-brands",
          component: () => import("./views/swell/BrandsView.vue"),
        },
        {
          path: "approvals",
          redirect: { name: "swell-brands" },
        },
        {
          path: "sources",
          name: "swell-sources",
          component: () => import("./views/swell/SourcesView.vue"),
        },
        {
          path: "settings",
          name: "swell-settings",
          component: () => import("./views/swell/SettingsView.vue"),
        },
      ],
      meta: {
        requiresAuth: true,
        isStaff: true,
        isSwell: true,
      },
    },
    {
      path: "/pulse",
      component: () => import("./views/pulse/PulseLayout.vue"),
      children: [
        { path: "", name: "pulse", redirect: { name: "pulse-overview" } },
        { path: "overview", name: "pulse-overview", component: () => import("./views/pulse/OverviewView.vue") },
        { path: "email", name: "pulse-email", component: () => import("./views/pulse/EmailView.vue") },
        { path: "chats", name: "pulse-chats", component: () => import("./views/pulse/ChatsView.vue") },
        { path: "calls", name: "pulse-calls", component: () => import("./views/pulse/CallsView.vue") },
        { path: "calendar", name: "pulse-calendar", component: () => import("./views/pulse/CalendarView.vue") },
        { path: "insights", name: "pulse-insights", component: () => import("./views/pulse/InsightsView.vue") },
        { path: "contacts", name: "pulse-contacts", component: () => import("./views/pulse/ContactsView.vue") },
        { path: "admin", name: "pulse-admin", component: () => import("./views/pulse/AdminView.vue") },
      ],
      meta: {
        requiresAuth: true,
        isStaff: true,
        isPulse: true,
      },
    },
    {
      // Client review link: public, token-only. Data access is through the
      // client_review_* RPCs, which validate the token server-side.
      path: "/r/:token",
      name: "client-review",
      component: () => import("./views/review/ClientReviewView.vue"),
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: () => import("./views/404View.vue"),
    },
  ];

export const router = createRouter({
    history: createWebHistory(),
    routes: routes,
    scrollBehavior (to, from, savedPosition) {
        if (savedPosition)
            return savedPosition
        else
            return { left: 0, top: 0 };
    }
});

// --- Stale-deploy / chunk-load recovery -------------------------------------
// When a new build is deployed, the content-hashed chunk files from the build
// a user currently has open can disappear from the server. A lazy-loaded route
// import then fails with a ChunkLoadError, or the browser refuses an index.html
// that was served (with a text/html MIME) in place of the missing .js file.
// We recover by reloading once to pull the fresh index.html and its new chunk
// filenames. A sessionStorage guard prevents an infinite reload loop when the
// failure is genuine (e.g. a real network outage rather than a deploy).
const CHUNK_RELOAD_KEY = "chunk-reload-attempted";

function isChunkLoadError(err) {
  const msg = (err && (err.message || String(err))) || "";
  return (
    err?.name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Failed to load module script/i.test(msg) ||
    /Loading module from .* was blocked/i.test(msg) ||
    /'text\/html' is not executable/i.test(msg) ||
    // Every browser words the "got HTML instead of JS/CSS" complaint
    // differently AND quotes the type differently:
    //   Chrome (stylesheet): MIME type ('text/html')
    //   Chrome (module):     MIME type of "text/html"
    //   Firefox:             disallowed MIME type (“text/html”)   <- curly quotes
    // Matching a specific quoting style is why this used to miss the module
    // variant, so just look for the two parts anywhere in the message.
    (/MIME type/i.test(msg) && /text\/html/i.test(msg))
  );
}

// Guarded single reload. Returns true if it triggered a recovery reload.
function recoverFromChunkError(targetPath) {
  let alreadyTried = false;
  try {
    alreadyTried = sessionStorage.getItem(CHUNK_RELOAD_KEY) === "1";
  } catch (_) {
    // sessionStorage can throw (private mode / disabled storage); reload anyway
  }

  if (alreadyTried) {
    // A second failure right after reloading — don't loop; let it surface.
    try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch (_) { /* noop */ }
    return false;
  }

  try { sessionStorage.setItem(CHUNK_RELOAD_KEY, "1"); } catch (_) { /* noop */ }

  // Reload straight to the intended destination so the user lands where they
  // were headed with a fully fresh set of assets.
  const target =
    targetPath ||
    window.location.pathname + window.location.search + window.location.hash;
  window.location.assign(target);
  return true;
}

router.onError((error, to) => {
  if (!isChunkLoadError(error)) {
    return; // let genuine app errors surface to Sentry / the console
  }
  recoverFromChunkError(to && to.fullPath);
});

// Safety net: router.onError only sees failures raised during a navigation.
// A chunk that fails outside routing — an async component, or an import() in a
// service/store — surfaces as an unhandled rejection instead, so it needs the
// same recovery or the user is left on a half-broken page after a deploy.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      recoverFromChunkError();
    }
  });
}

// Once any navigation completes successfully the app is on a consistent build,
// so clear the guard — a *later* deploy in the same tab can then trigger its
// own single recovery reload.
router.afterEach(() => {
  try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch (_) { /* noop */ }
});
// ---------------------------------------------------------------------------

router.beforeEach(async (to) => {
  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth);
  const isStaff = to.matched.some((r) => r.meta.isStaff);


  if (requiresAuth && !store.authUser) {
    return { name: "login" };
  }

  if (to.name === "login" && store.authUser) {
    return { name: "index" };
  }

  if (isStaff && store.currentUser?.user_type !== "staff") {
    return { name: "not-found" };
  }

  // Swell: must be a member; and each section is gated to the member's permission.
  const isSwell = to.matched.some((r) => r.meta.isSwell);
  if (isSwell) {
    const perms = swell.permsLoaded ? swell.perms : await swell.loadPerms();
    if (!perms) return { name: "not-found" }; // staff, but not granted Swell access
    const SECTIONS = ["leads", "pipeline", "insights", "roster", "contacts", "brands", "discovery", "settings"];
    const section = (to.name || "").replace("swell-", "");
    if (SECTIONS.includes(section) && !swell.can(section)) {
      const first = SECTIONS.find((s) => swell.can(s));
      return first ? { name: "swell-" + first } : { name: "not-found" };
    }
  }

  // Pulse: must be a member; each section is gated to the member's access.
  const isPulse = to.matched.some((r) => r.meta.isPulse);
  if (isPulse) {
    const perms = pulse.permsLoaded ? pulse.perms : await pulse.loadPerms();
    if (!perms) return { name: "not-found" }; // staff, but not granted Pulse access
    const SECTIONS = ["overview", "email", "chats", "calls", "calendar", "insights", "contacts", "admin"];
    const section = (to.name || "").replace("pulse-", "");
    if (SECTIONS.includes(section) && !pulse.can(section)) {
      const first = SECTIONS.find((s) => pulse.can(s));
      return first ? { name: "pulse-" + first } : { name: "not-found" };
    }
  }

  const requiresStarsMagnet = to.matched.some((r) => r.meta.requiresStarsMagnet);
  if (requiresStarsMagnet) {
    const access = await smAccess.load();
    if (!access.member) return { name: "not-found" };
  }

  const requiresLeave = to.matched.some((r) => r.meta.requiresLeave);
  if (requiresLeave && !canUseLeave()) {
    return { name: "not-found" };
  }

  const requiresHigherManagement = to.matched.some((r) => r.meta.requiresHigherManagement);
  if (requiresHigherManagement && !higherManagementUserIds.includes(store.currentUser?.id)) {
    return { name: "not-found" };
  }
});


export default router;
